<script setup lang="ts">
import { useGetAdminJob } from '@swipe4work/api-orval'
import type { GetAdminJobJobAdminDetailDto } from '@swipe4work/models-orval'
import { useRouter } from 'vue-router'
import VacatureDetailHeader from '@/features/vacatures/components/VacatureDetailHeader.vue'
import { usePermissions } from '@/shared/composables/UsePermissions'
import { useTopBarTitle } from '@/shared/composables/UseTopBarTitle'

definePage({
  path: '/vacatures/:id',
  name: 'vacatures.detail',
  meta: {
    title: 'Vacature',
    breadcrumbParent: 'vacatures.overview',
    permission: {
      resource: 'Job',
      level: 'Read',
    },
  },
})

const route = useRoute('vacatures.detail')
const router = useRouter()
const jobId = computed(() => Number((route.params as { id: string }).id))

const { data, isPending } = useGetAdminJob(jobId, { query: { staleTime: 5 * 60 * 1000 } })

const job = computed(() => data.value?.job ?? null)

// The topbar shows the job title as the last breadcrumb once loaded.
useTopBarTitle(() => job.value?.title)

provide<ComputedRef<GetAdminJobJobAdminDetailDto | null>>('jobDetail', job)
provide<Ref<boolean>>('jobIsPending', isPending)
provide<ComputedRef<number>>('jobId', jobId)

onBeforeMount(() => {
  if (route.name === 'vacatures.detail') {
    router.replace({
      name: 'vacatures.detail.overview' as any,
      params: { id: jobId.value },
    })
  }
})

watch(() => route.name, (name) => {
  if (name === 'vacatures.detail') {
    router.replace({
      name: 'vacatures.detail.overview' as any,
      params: { id: jobId.value },
    })
  }
})

const { can } = usePermissions()

const tabs = computed(() => [
  {
    label: 'Details',
    name: 'vacatures.detail.overview',
  },
  {
    label: 'Kandidaten',
    name: 'vacatures.detail.candidates',
  },
  {
    label: 'Matchscores',
    name: 'vacatures.detail.match-scores',
  },
  {
    label: 'Algoritme vergelijker',
    name: 'vacatures.detail.match-score-statistics',
  },
  ...(can('JobStatistics', 'Read')
    ? [
      {
        label: 'Vacature statistieken',
        name: 'vacatures.detail.statistics',
      },
    ]
    : []),
])
</script>

<template>
  <div class="space-y-5">
    <VacatureDetailHeader :job="job" />

    <!-- Tabs -->
    <Tabs :value="route.name" scrollable>
      <TabList>
        <router-link
          v-for="tab in tabs"
          :key="tab.name"
          :to="{ name: tab.name as any, params: { id: jobId } }"
        >
          <Tab :value="tab.name">
            {{ tab.label }}
          </Tab>
        </router-link>
      </TabList>
    </Tabs>

    <router-view v-slot="{ Component }">
      <component :is="Component" />
    </router-view>
  </div>
</template>
